import React, { useCallback, useContext, useEffect, useState } from "react";
import classes from "./CollaborateMode.module.scss";
import * as Icons from "Components/Icons";
import { User, UserInput } from "Components/User";
import { useActivity } from "../hooks";
import { useApiClient } from "Services/API/useApiClient";
import { ActivityReservationInternalClient, ActivityReservationInternalModel, CurrentLocationSearchRequest, FullTextSearchRequest, IBaseSearchRequest, ICurrentLocationSearchRequest, IFullTextSearchRequest, ILocationViewModel, MembersClient, OrganizerViewModel, ReservationMethod, SearchClient, SpaceType, VenueModel, VenuesClient, VenueSearchResult, VenueWorkspaceModel, WorkspacesClient, WorkspaceSearchResult } from "Services/API/API";
import UserContext from "features/EnterprisePortal/UserContext";
import { Button, Modal, ModalBody, ModalHeader, UncontrolledTooltip } from "Components/ReactstrapSlim";
import { formatStringDate, toDateOnly } from "Utils/date";
import { LayoutContext } from "Components/LayoutContext";
import LsLoading, { useLsLoading } from "Components/LsLoading";
import AccountContext from "features/EnterprisePortal/AccountContext";
import { NOT_DELETED_FILTER } from "Utils/ActivityReservationStatusFilterPresets";
import { DatePickerInput } from "Components/Inputs";
import { AvatarCircleWithCache } from "Components/AvatarCircle";
import { PlacesAutocomplete } from "Components/Autocompletes/PlacesAutocomplete";
import { parsePlaceToAddress } from "Utils/parsePlaceToAddress";
import classNames from "classnames";
import { UserPictureCache } from "Hooks";
import { formatTime } from "Utils/datetime";
import WorkspaceCard from "Components/WorkspaceCard";
import NullSearchResults from "./NullSearchResults";
import { BIN } from "features/ComponentsLib";


interface ActivitiesByVenueId {
   id: Guid,
   activities: ActivityReservationInternalModel[]
}

interface VenueWorkspaceWithActivies {
   venue: VenueSearchResult | VenueModel,
   workspace: WorkspaceSearchResult,
   activities: ActivityReservationInternalModel[]
}

interface VenueWorkspaceSearchResultWithGuestInfo {
   venue: VenueSearchResult,
   workspace: WorkspaceSearchResult,
   guests: Guid[]
}

interface Filter {
   guests: User[],
   date: Date,
   spaceType: SpaceType,
   location?: string
}

interface CollaborateFilterProps {
   workMode: WorkMode,
   filter?: Filter,
   applyFilter: (filter: Filter) => void,
   connections: OrganizerViewModel[],
}

export enum WorkMode {
   Focus = "Focus",
   Cowork = "Cowork",
   Collaborate = "Collaborate"
}

const SocialCollabFilter: React.FC<CollaborateFilterProps> = ({ workMode, filter, applyFilter, connections }) => {
   const { userPictureCache } = useActivity();
   const { enterpriseUser } = useContext(UserContext);
   const [inputKey, setInputKey] = useState<number>(0);
   const [guests, setGuests] = useState<User[]>(workMode != WorkMode.Focus ? filter?.guests ?? [] : []);
   const [date, setDate] = useState<Date>(filter?.date ?? new Date());
   const [location, setLocation] = useState(filter?.location ?? enterpriseUser.homeBaseSearchAddress ?? "");
   const [errorMessage, setErrorMessage] = useState<string>();
   const getUsers = useCallback((text: string) => {
      const users = connections.map((v, _) => ({
         id: v.id,
         fullName: `${v.firstName} ${v.lastName}`,
         email: v.email,
         avatar: v.imageUrl
      } as User));

      const searchText = text.toLowerCase();
      return users.filter(user => user.fullName?.toLowerCase()?.includes(searchText) || user?.email?.toLowerCase()?.includes(searchText));
   }, [connections]);


   const addGuest = (guest: User) => {
      if (guests.findIndex(g => g.id == guest.id) < 0) {
         setGuests(guests => [guest, ...guests]);
         setInputKey(k => k + 1);
      } else {
         setErrorMessage("Member has been added");
      }
   }

   const removeGuest = (guest: User) => {
      setGuests(guests => [...guests.filter(v => v != guest)]);
   }

   const onDateSelected = (newDate: Date) => {
      setDate(newDate);
   }

   const extractCity = (place: ILocationViewModel) => {
      const { city, state } = parsePlaceToAddress(place);
      if (state)
         setLocation(`${city.longText}, ${state.shortText}`);
      else
         setLocation(`${city.longText}`);
   }

   const getSpaceType = () => {
      switch (workMode) {
         case WorkMode.Focus:
            return SpaceType.Desk | SpaceType.Office;
         case WorkMode.Cowork:
            return SpaceType.Desk;
         case WorkMode.Collaborate:
            return SpaceType.Office | SpaceType.Meeting;
         default:
            return SpaceType.Any;
      }
   }

   const onApply = () => {
      const newFilter = {
         guests: guests,
         date: date,
         location: location,
         spaceType: getSpaceType(),
      };
      applyFilter(newFilter);
   }

   return <div className={classes.filterWrapper}>
      <div>
         {workMode != WorkMode.Focus && <><div className={classes.filterHeader}>Find people to work with</div>
            <div className={classes.filterBody}>
               <UserInput
                  key={inputKey}
                  userPictureCache={userPictureCache}
                  onClick={(user) => addGuest(user)}
                  placeholder="Name, Email"
                  getUsers={getUsers}
                  errorMessage={errorMessage}
                  onChange={() => setErrorMessage(undefined)}
               />
               <div className={classes.filterGuestList}>
                  {guests.map(g => {
                     const [firstName, lastName] = g.fullName?.split(" ") ?? [];
                     return <div key={g.id || g.email} className={classes.filterGuest}>
                        <AvatarCircleWithCache
                           firstName={firstName}
                           lastName={lastName}
                           email={g.email}
                           userId={g.id ?? Guid.Empty}
                           cache={userPictureCache}
                           size="lg"
                           className={classes.filterGuestAvatar}
                        />
                        <div className={classes.filterGuestInfo}>
                           <div className={classes.filterGuestTitle}>{g.fullName}</div>
                           <div className={classes.filterGuestSubtitle}>{g.email}</div>
                        </div>
                        <div className={classes.filterGuestRemove}>
                           <Button size="icon" className={classes.close} onClick={() => removeGuest(g)}>
                              <Icons.Close />
                           </Button>
                        </div>
                     </div>
                  })}
               </div>
            </div>
         </>}
         <div className={classes.filterRow}>
            <div>
               <div className={classes.filterHeader}>Choose a date</div>
               <div className={classes.filterBody}>
                  <DatePickerInput
                     name="date-picker"
                     onChange={onDateSelected}
                     selected={date}
                     minDate={new Date()}
                     placeholderText="Date"
                     useFloatLabel
                  //calendarClassName={classes.datePicker}                                             
                  />
               </div>
            </div>
            <div>
               <div className={classes.filterHeader}>Choose a location</div>
               <div className={classes.filterBody}>
                  <PlacesAutocomplete
                     value={location}
                     onChange={v => setLocation(v || "")}
                     onItemSelected={extractCity}
                     placeholder="City"
                  />
               </div>
            </div>
         </div>
      </div>
      <div className={classes.filterCtaWrapper}>
         <Button onClick={onApply} color="primary" disabled={!!errorMessage}>Next</Button>
      </div>
   </div>
}


interface GuestAvatarListProps {
   guests: User[],
   cache: UserPictureCache,
   className?: string
   id?: string
}
const GuestAvatarList: React.FC<GuestAvatarListProps> = ({ guests, cache, className, id }) => {
   return <div className={classNames(classes.filterDisplayAvatarsWrapper, className)} id={id}>
      {guests.map((col, index) => {
         const [firstName, lastName] = col.fullName?.split(" ") ?? [];
         return <AvatarCircleWithCache
            key={col.email}
            cache={cache}
            userId={col.id ?? Guid.Empty}
            noborder={index == 0}
            size="md"
            email={col.email}
            firstName={firstName}
            lastName={lastName}
            className={classes.avatar}
         />
      })}
   </div>
}

interface SocialCollabModeProps {
   workMode: WorkMode;
   onBookingComplete?: () => void,
}

const SocialCollabMode: React.FC<SocialCollabModeProps> = ({ workMode, onBookingComplete }) => {
   const layoutContext = useContext(LayoutContext);
   const { userPictureCache, reloadActivities, modalContainer } = useActivity();
   const memberClient = useApiClient(MembersClient);
   const searchClient = useApiClient(SearchClient);
   const activityInternalClient = useApiClient(ActivityReservationInternalClient);
   const venueClient = useApiClient(VenuesClient);
   const workspaceClient = useApiClient(WorkspacesClient);
   const { user } = useContext(UserContext);
   const account = useContext(AccountContext);
   const [_wrapperClass, isLoading, setIsLoading] = useLsLoading(false);

   const [connections, setConnections] = useState<OrganizerViewModel[]>([]);
   const [filter, setFilter] = useState<Filter>();
   const [venueWorkspaces, setVenueWorkspaces] = useState<VenueWorkspaceSearchResultWithGuestInfo[]>([]);
   const [sameDateVenueWorkspaces, setSameDateVenueWorkspaces] = useState<VenueWorkspaceWithActivies[]>([]);
   const [showFilterModal, setShowFilterModal] = useState(false);
   const [showFilterStep, setShowFilterStep] = useState(true);

   const filterDateOnly = filter?.date ? toDateOnly(filter.date) : "";
   const filterDateFormatted = filterDateOnly ? formatStringDate(filterDateOnly, "dddd, MMM D") : "";

   const toWorkspaceResult = (workspace: VenueWorkspaceModel): WorkspaceSearchResult => {
      return {
         id: workspace.id,
         name: workspace.name,
         priceAmount: workspace.pricesFormatted,
         spaceTypeFormatted: workspace.spaceTypeFormatted,
         averageRating: workspace.averageRating
      } as WorkspaceSearchResult;
   }

   const applyFilters = async (newFilter: Filter) => {
      setShowFilterModal(false);
      setFilter(newFilter);
      setShowFilterStep(false);
      setIsLoading(true);

      try {
         const [searchResult, guestActivities] = await Promise.all([
            search(newFilter),
            loadGuestActivities(newFilter)
         ]);
         //console.log("guestActivities", guestActivities);

         const filterDateOnly = toDateOnly(newFilter.date);
         const sameDateActivities = guestActivities.filter(a => toDateOnly(a.startDate) == filterDateOnly);

         if (sameDateActivities.length) {
            const activitiesByVenueId = sameDateActivities.reduce<ActivitiesByVenueId[]>((res, activity) => {
               const venueResult = res.find(r => r.id == activity.venueId)
               if (venueResult) {
                  venueResult.activities.push(activity);
               }
               else {
                  res.push({ id: activity.venueId, activities: [activity] });
               }
               return res;
            }, []);

            const sameDateVenues = await Promise.all(
               activitiesByVenueId.map(vAcc => {
                  return (async () => {
                     return {
                        venue: await venueClient.get(vAcc.id),
                        workspaces: await Promise.all(vAcc.activities.map(activity => workspaceClient.get(activity.venueId, activity.workspaceId))),
                        activities: vAcc.activities
                     }
                  })();
               }));

            const sameDateWorkspaces: VenueWorkspaceWithActivies[] = [];
            sameDateVenues.forEach(vAcc => {
               vAcc.workspaces.forEach(w => {
                  sameDateWorkspaces.push({
                     venue: vAcc.venue,
                     workspace: toWorkspaceResult(w),
                     activities: vAcc.activities.filter(acc => acc.workspaceId == w.id),
                  });
               })
            });

            setSameDateVenueWorkspaces(sameDateWorkspaces);
            //console.log("sameDateVenues", sameDateVenues);
            //console.log("sameDateWorkspaces", sameDateWorkspaces);
         }

         const workspacesWithGuests: { [key: Guid]: Guid[] } = {};
         guestActivities.forEach(a => {
            if (!workspacesWithGuests[a.workspaceId])
               workspacesWithGuests[a.workspaceId] = [];
            if (workspacesWithGuests[a.workspaceId].indexOf(a.memberId) == -1)
               workspacesWithGuests[a.workspaceId].push(a.memberId);
         });

         const sameDateWorkspaceIds = sameDateActivities
            .map(w => w.workspaceId)
            .filter((val, index, self) => self.indexOf(val) === index);

         const workspaceList: VenueWorkspaceSearchResultWithGuestInfo[] = [];
         searchResult.venues
            .forEach(v => {
               let maxCount = 0;
               v.workspaceSearchResults.forEach(w => {
                  const isVisible = sameDateWorkspaceIds.indexOf(w.id) == -1 && (w.spaceType & newFilter.spaceType) != SpaceType.Any;
                  if (isVisible && maxCount < 2) {
                     const result: VenueWorkspaceSearchResultWithGuestInfo = {
                        workspace: w,
                        venue: v,
                        guests: workspacesWithGuests[w.id] ?? []
                     }
                     workspaceList.push(result);
                     maxCount++;
                  }
               })
            });
         workspaceList.sort((a, b) => b.guests.length - a.guests.length);
         setVenueWorkspaces(workspaceList);
         //console.log("workspace list: ", workspaceList);
      }
      catch (ex) {
         layoutContext.showError(ex);
      }
      finally {
         setIsLoading(false)
      }
   }

   const search = async (filter: Filter) => {
      const baseSearchRequest: Partial<IBaseSearchRequest> = {
         reservationMethod: ReservationMethod.Hourly,
         minCapacity: filter.guests.length > 0 ? filter.guests.length + 1 : undefined,
      };
      const request = filter.location
         ? new FullTextSearchRequest({
            fullTextQuery: filter.location,
            spaceTypes: filter.spaceType,
            ...baseSearchRequest
         } as IFullTextSearchRequest)
         : new CurrentLocationSearchRequest({
            spaceTypes: filter.spaceType,
            ...baseSearchRequest
         } as ICurrentLocationSearchRequest);

      return await searchClient.execute(request);
   }

   const loadGuestActivities = async (filter: Filter) => {
      const referenceDate = filter.date;
      const startDate = new Date();
      const endDate = new Date();
      startDate.setDate(referenceDate.getDate() - 30);
      endDate.setDate(referenceDate.getDate() + 30);
      const result = await activityInternalClient.reservations(
         account.id,
         NOT_DELETED_FILTER,
         undefined,
         undefined,
         toDateOnly(startDate),
         toDateOnly(endDate),
         undefined,
      );

      const memberIds = new Set(filter.guests.map(g => g.id));
      //console.log("memberIds", filter.guests, memberIds);
      return result.filter(r => memberIds.has(r.memberId));
   }

   const openFilter = () => {
      setShowFilterModal(true);
   }

   const toggleFilter = () => {
      setShowFilterModal(false);
   }

   const showBin = (venueId: string, workspaceId: string) => {
      BIN({
         type: "NEW",
         venueId: venueId,
         workspaceId: workspaceId,
         startDate: toDateOnly(filter?.date),
         guests: filter?.guests
            .filter(g => !!g.email && !!g.fullName)
            .map(g => ({ email: g.email!, fullName: g.fullName! })),
         onClose: (isConfirmed) => {
            if (isConfirmed) {
               reloadActivities();
               if (onBookingComplete)
                  onBookingComplete();
            }
         }
      });
   }

   const toggleBack = () => {
      setShowFilterStep(true);
   }

   useEffect(() => {
      const loadConnections = async (signal?: AbortSignal) => {
         try {
            const result = await memberClient.connections(user.id, signal);
            setConnections(result);
         } catch (ex) {
            if (signal?.aborted)
               return;
            console.log("Error loading connections for owner: ", ex);
         }
      }

      const abortController = new AbortController();
      loadConnections(abortController.signal);
      return () => {
         abortController.abort();
      }
   }, [])

   return (
      <>
         {!showFilterStep && !!filter && <div className={classes.wrapper}>
            <div className={classes.filterDisplay} onClick={openFilter}>
               <div>{WorkMode[workMode]}{filter.guests.length > 0 ? " with" : ""}</div>
               {filter.guests.length > 0 && <GuestAvatarList guests={filter.guests} cache={userPictureCache} />}
               <div>on {filterDateFormatted}</div>
               {filter.location && <div>in {filter.location}</div>}
            </div>
            <div className={classNames(classes.results, { [classes.resultsLoading]: isLoading })}>
               {
                  isLoading
                     ? <LsLoading isLoading />
                     : <>
                        {
                           sameDateVenueWorkspaces.length > 0 && <>
                              <div className={classes.resultHeader}>Some of your colleagues have already booked this day</div>
                              <div className={classes.sameDateResult}>{sameDateVenueWorkspaces.map(vw =>
                                 <WorkspaceCard key={vw.workspace.id} container={modalContainer} venue={vw.venue} workspace={vw.workspace} onClick={() => showBin((vw.venue as VenueModel).id, vw.workspace.id)} onWorkHereClick={() => showBin((vw.venue as VenueModel).id, vw.workspace.id)}>
                                    {vw.activities.length > 0 && <>
                                       <GuestAvatarList
                                          guests={vw.activities.map((a): User => {
                                             return {
                                                fullName: a.memberFullName ?? "",
                                                email: a.memberEmail,
                                                id: a.memberId
                                             }
                                          })}
                                          id={`gt-${vw.workspace.id}`}
                                          className={classes.venueCardGuestList}
                                          cache={userPictureCache} />
                                       <UncontrolledTooltip placement="top" target={`gt-${vw.workspace.id}`} fade={false}>
                                          {vw.activities.map(a => <div key={a.reservationId}>{a.memberFullName}: {formatTime(a.startDate)}{a.endDate ? ` - ${formatTime(a.endDate)}` : " (autorenew)"}</div>)}
                                       </UncontrolledTooltip >
                                    </>}
                                 </WorkspaceCard>)}</div>
                              {venueWorkspaces.length > 0 && <div className={classes.resultHeader}>Other workspaces</div>}
                           </>
                        }
                        {
                           venueWorkspaces.length > 0
                              ? <div className={classes.searchResult}>
                                 {venueWorkspaces.map(vw => {
                                    const guestsInWorkspace = vw.guests.length > 0 ? filter.guests.filter(g => vw.guests.indexOf(g.id ?? Guid.Empty) > -1) : [];
                                    return <WorkspaceCard key={vw.workspace.id} container={modalContainer} venue={vw.venue} workspace={vw.workspace} onClick={() => showBin(vw.venue.venueId, vw.workspace.id)} onWorkHereClick={() => showBin(vw.venue.venueId, vw.workspace.id)} >
                                       {guestsInWorkspace.length > 0 && <>
                                          <GuestAvatarList
                                             guests={guestsInWorkspace}
                                             className={classes.venueCardGuestList}
                                             id={`gr-${vw.venue.venueId}`}
                                             cache={userPictureCache} />
                                          <UncontrolledTooltip placement="top" target={`gr-${vw.venue.venueId}`} fade={false}>
                                             {guestsInWorkspace.map(g => g.fullName).join(", ")} {guestsInWorkspace.length > 1 ? "have" : "has"} recently booked at this place
                                          </UncontrolledTooltip ></>}
                                    </WorkspaceCard>
                                 })}
                              </div>
                              : null
                        }
                        {sameDateVenueWorkspaces.length == 0 && venueWorkspaces.length == 0 &&
                           <NullSearchResults />
                        }
                     </>
               }
            </div>
            <div className={classes.searchResultCtaWrapper}>
               <Button onClick={toggleBack} color="primary">Back</Button>
            </div>
         </div>}
         {showFilterStep && <SocialCollabFilter filter={filter} workMode={workMode} applyFilter={applyFilters} connections={connections} />}
         {showFilterModal &&
            <Modal isOpen={true} toggle={toggleFilter} size="lg" container={modalContainer}>
               <ModalHeader toggle={toggleFilter}></ModalHeader>
               <ModalBody>
                  <SocialCollabFilter workMode={workMode} filter={filter} applyFilter={applyFilters} connections={connections} />
               </ModalBody>
            </Modal>
         }
      </>
   );
};

export default SocialCollabMode;
